import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { Link as RouterLink } from 'react-router-dom';

import { focusRing, hoverable, pressable } from '../../interaction-states';
import { ExpandToggle } from '../ExpandToggle';
import Typography from '../Typography';

export const CardRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  borderRadius: '16px',
  background: theme.colors.background.raised,
  overflow: 'hidden',
}));

export const CardIdentity = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  minWidth: 0,
});

export const CardTitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: 24,
  width: '100%',
});

export const CardTitle = styled(Typography)({
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const CardTitleIcon = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,

  svg: {
    width: 16,
    height: 16,
  },
});

export const CardSubtitle = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  color: theme.colors.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const titleActionStyles = (theme: Theme) => ({
  ...hoverable(theme, 'underline'),
  ...pressable(theme, 'underline'),
  ...focusRing(theme),
  display: 'block',
  flex: '1 0 0',
  minWidth: 0,
  borderRadius: theme.radius[8],
  color: 'inherit',
  textDecoration: 'none',
  cursor: 'pointer',
});

export const CardTitleBox = styled('div')({
  flex: '1 0 0',
  minWidth: 0,
});

export const CardTitleAction = styled('span')(({ theme }) => titleActionStyles(theme));

export const CardTitleLink = styled(RouterLink)(({ theme }) => titleActionStyles(theme));

export const CardToggle = styled(ExpandToggle)({
  padding: 4,
});

export const CardDetails = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  paddingTop: '16px',
});

export const CardDivider = styled('div')(({ theme }) => ({
  height: 1,
  background: theme.colors.border.default,
}));

export const FieldsGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
});

export const FieldRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== '$fullWidth',
})<{ $fullWidth?: boolean }>(({ $fullWidth }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
  overflow: 'hidden',
  ...($fullWidth && { gridColumn: '1 / -1' }),
}));

export const FieldValue = styled(Typography)({
  wordBreak: 'break-word',
});
